// src/tabs/ProfileTab.jsx
import React, { useContext, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Button,
  Divider,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AddIcon from "@mui/icons-material/Add";
import { formatDate } from "../../../utils/helpers";
import { useOutletContext } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";


/**
 * ProfileTab
 * - User info
 * - Purchase summary
 * - Addresses (UI-only)
 * - Support & legal
 */
export default function ProfileTab() {

  const {
    devices,
    addresses,
    retailerInfo,
    onAddAddress,
    onEditAddress,
    onViewPurchase,
    onDeleteAddress,
    deviceinprofile,
  } = useOutletContext();

  const { user, logout } = useContext(AuthContext);
  /** 🔹 MOCK PURCHASES */
  const purchases = [
    {
      id: "pur-1",
      model_name: "Google Pixel 8",
      purchase_date: "2025-12-20",
      order_no: "ORD-77881",
      serial_no: "SN-PXL8-88991234",
      imei: "356789101112131",
      warranty_expiry: "2026-12-20",
      purchased_from: {
        name: "Akash Mobile World",
      },
    },
    {
      id: "pur-2",
      model_name: "Samsung Galaxy S24",
      purchase_date: "2025-11-08",
      order_no: "ORD-66420",
      serial_no: "SN-S24-10012025",
      imei: "357891011121314",
      warranty_expiry: "2026-11-08",
      purchased_from: {
        name: "City Mobiles",
      },
    },
  ];

  /** 🔹 MOCK ADDRESSES */

  const purchasesSorted = useMemo(() => {
    return [...purchases].sort(
      (a, b) => new Date(b.purchase_date) - new Date(a.purchase_date)
    );
  }, []);

  const uniqueShopCount = new Set(
    purchases.map((p) =>
      p.purchased_from?.name?.toLowerCase()
    )
  ).size;

  return (
    <Stack spacing={2}>
      {/* ================= PROFILE ================= */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack alignItems="center" spacing={2}>
            <Avatar sx={{ width: 80, height: 80 }}>
              {(user?.name || "U").charAt(0)}
            </Avatar>

            <Typography fontWeight={900}>
              {user?.name || "User"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              {user?.email || user?.phone || "—"}
            </Typography>

            <Button
              startIcon={<PersonIcon />}
              sx={{ borderRadius: 3 }}
            >
              Edit Profile
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ================= PURCHASE SUMMARY ================= */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight={900}>
              Warranty Purchases
            </Typography>

            <Button
              size="small"
              startIcon={<ListAltIcon />}
              onClick={onViewPurchase}
            >
              View All
            </Button>

          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {purchases.length} purchase
            {purchases.length !== 1 ? "s" : ""} •{" "}
            {uniqueShopCount} shop
            {uniqueShopCount !== 1 ? "s" : ""}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Stack spacing={1.2}>
            {devices.slice(0, 3).map((p) => (
              <Card
                key={p.id}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Typography fontWeight={900}>
                    {p.brand_name} {p.model}
                  </Typography>

                  <Typography fontWeight={400}>
                    {p.product_name}
                  </Typography>

                  <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {retailerInfo?.business_name || "—"} •{" "}
                      {p.created_at ? formatDate(p.created_at) : "—"}
                    </Typography>


                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Order No: {p.w_code} • S/N:{" "}
                    {p.serial}
                    {p.imei1 &&
                      ` • IMEI: ${p.imei1}`}
                    {p.expiry_date &&
                      ` • Expiry: ${formatDate(
                        p.expiry_date
                      )}`}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* ================= ADDRESSES ================= */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight={900}>
              My Addresses
            </Typography>

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddAddress}
            >
              Add
            </Button>

          </Stack>

          <Divider sx={{ my: 1 }} />

         {addresses.map((a) => (
            <Card key={a.id} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography fontWeight={900}>{a.name}</Typography>

                <Typography variant="body2" color="text.secondary">
                  {a.mobile}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {a.address1}
                  {a.address2 ? `, ${a.address2}` : ""}
                  , {a.city}, {a.state} {a.pincode}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" onClick={() => onEditAddress(a)}>
                    Edit
                  </Button>

                  <Button
                    size="small"
                    color="error"
                    onClick={() => onDeleteAddress(a.id)}
                  >
                    Delete
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}

        </CardContent>
      </Card>

      {/* ================= SUPPORT ================= */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography fontWeight={900}>
            Support
          </Typography>

          {/* 📞 CALL */}
          <Typography
            variant="body2"
            component="a"
            href="tel:+919321868767"
            sx={{
              display: "block",
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            📞 93218 68767
          </Typography>

          {/* ✉️ EMAIL */}
          <Typography
            variant="body2"
            component="a"
            href="mailto:hello@goelectronix.com"
            sx={{
              display: "block",
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            ✉️ hello@goelectronix.com
          </Typography>
        </CardContent>
      </Card>

      {/* ================= LEGAL ================= */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography fontWeight={900}>
            Legal
          </Typography>

          {/* Privacy Policy */}
          <Typography
            variant="body2"
            component="a"
            href="https://www.goelectronix.com/privacy.php"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "block",
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Privacy Policy
          </Typography>

          {/* Terms & Conditions */}
          <Typography
            variant="body2"
            component="a"
            href="https://www.goelectronix.com/termandcondition.php"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "block",
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Terms & Conditions
          </Typography>

          {/* Return & Refund */}
          <Typography
            variant="body2"
            component="a"
            href="https://www.goelectronix.com/return-refund-policy.php"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "block",
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Return & Refund
          </Typography>
        </CardContent>
      </Card>


    </Stack>
  );
}
